import { EstimatorConfig, EstimatorSelection, PriceBreakdown, TimelineBreakdown } from '../types';

// 外注費と工数を正確に計算する最適化価格計算システム
// 月間固定費30万円を基準に、ギリギリ黒字ラインの価格算出

type ServiceCosts = Record<string, number>;

interface CostAnalysis {
  fixedCost: number;
  outsourcingCost: number;
  totalCost: number;
  profitMargin: number;
  finalPrice: number;
  workDays: number;
  description: string;
}

interface OptimizedPriceBreakdown extends PriceBreakdown {
  costAnalysis: CostAnalysis;
  traditionalPrice: number;
  costReduction: number;
  costReductionPercentage: number;
}

// 共通コスト定義
const FIXED_COSTS = {
  MONTHLY_FIXED: 300000, // 月間固定費
  DAILY_FIXED: 10000,    // 日割り固定費
  MIN_PROFIT_MARGIN: 0.05, // 最小利益率5%
  TAX_RATE: 0.1,         // 消費税10%
};

// サービス別外注費・工数データ
const SERVICE_COSTS = {
  'music-services': {
    MIDI_OUTSOURCING: 15000,
    VOCAL_RECORDING: 2,
    PARAMETER_ADJUSTMENT: 2,
    MIXING_MASTERING: 3,
    ADDITIONAL_VOCAL: 1,
    VOCAL_ONLY: 3,
    MIXING_ONLY: 2,
  },
  'ai-copywriting': {
    SINGLE_ARTICLE: 1,
    WEEKLY_CONTENT: 3,
    MONTHLY_PACKAGE: 7,
    URGENT_DELIVERY: 0.5,
    MULTILINGUAL: 1,
  },
  'brand-design': {
    LOGO_DESIGN: 2,
    BRAND_IDENTITY: 5,
    COMPREHENSIVE_BRAND: 10,
    AI_IMAGE_GENERATION: 1,
    ANIMATION_WORK: 2,
  },
  'system-development': {
    LANDING_PAGE: 3,
    CORPORATE_SITE: 10,
    WEB_APPLICATION: 20,
    AI_INTEGRATION: 5,
    MOBILE_APP: 15,
  },
  'ai-consulting': {
    QUICK_ASSESSMENT: 3,
    COMPREHENSIVE_STRATEGY: 7,
    IMPLEMENTATION_SUPPORT: 14,
    POC_DEVELOPMENT: 5,
    TEAM_TRAINING: 2,
  },
};

// 工数・外注費計算関数
function calculateDetailedCost(
  config: EstimatorConfig,
  selection: EstimatorSelection
): CostAnalysis {
  const selectedPackage = config.packages.find(p => p.id === selection.packageId);
  if (!selectedPackage) {
    throw new Error('Invalid package selection');
  }

  // サービス別の工数・外注費計算
  let workDays = 0;
  let outsourcingCost = 0;
  let description = '';

  const serviceCosts = SERVICE_COSTS[config.id.split('-')[0] as keyof typeof SERVICE_COSTS] || {};

  // パッケージ別の工数・外注費計算
  switch (config.id) {
    case 'music-services-optimized':
      workDays = calculateMusicWorkDays(selectedPackage.id, selection.optionIds, serviceCosts);
      outsourcingCost = calculateMusicOutsourcingCost(selectedPackage.id, selection.optionIds, serviceCosts);
      description = `音楽制作: ${workDays}日間の作業`;
      break;
    
    case 'ai-copywriting-optimized':
      workDays = calculateCopywritingWorkDays(selectedPackage.id, selection.optionIds, serviceCosts);
      description = `コンテンツ制作: ${workDays}日間の作業`;
      break;
    
    case 'brand-design-optimized':
      workDays = calculateDesignWorkDays(selectedPackage.id, selection.optionIds, serviceCosts);
      description = `デザイン制作: ${workDays}日間の作業`;
      break;
    
    case 'system-development-optimized':
      workDays = calculateDevelopmentWorkDays(selectedPackage.id, selection.optionIds, serviceCosts);
      description = `システム開発: ${workDays}日間の作業`;
      break;
    
    case 'ai-consulting-optimized':
      workDays = calculateConsultingWorkDays(selectedPackage.id, selection.optionIds, serviceCosts);
      description = `コンサルティング: ${workDays}日間の作業`;
      break;
    
    default:
      // フォールバック計算
      workDays = config.timeline.typical;
      description = `標準作業: ${workDays}日間`;
  }

  // 数量による調整
  if (selection.quantity > 1) {
    // スケールメリット: 2件目以降は70%の工数
    const additionalWorkDays = (selection.quantity - 1) * workDays * 0.7;
    workDays += additionalWorkDays;
  }

  // 緊急度による調整
  if (selection.urgency === 'urgent') {
    workDays *= 0.7; // 作業を圧縮
  } else if (selection.urgency === 'flexible') {
    workDays *= 1.2; // 余裕を持った作業
  }

  // コスト計算
  const fixedCost = Math.round(workDays * FIXED_COSTS.DAILY_FIXED);
  const totalCost = fixedCost + outsourcingCost;
  const profitMargin = Math.round(totalCost * FIXED_COSTS.MIN_PROFIT_MARGIN);
  const finalPrice = totalCost + profitMargin;

  return {
    fixedCost,
    outsourcingCost,
    totalCost,
    profitMargin,
    finalPrice,
    workDays: Math.round(workDays * 10) / 10,
    description
  };
}

// 音楽制作の工数計算
function calculateMusicWorkDays(packageId: string, optionIds: string[], costs: ServiceCosts): number {
  let baseDays = 0;
  
  switch (packageId) {
    case 'vocals-only':
      baseDays = costs.VOCAL_ONLY || 3;
      break;
    case 'mixing-only':
      baseDays = costs.MIXING_ONLY || 2;
      break;
    case 'full-production':
      baseDays = (costs.VOCAL_RECORDING || 2) + (costs.PARAMETER_ADJUSTMENT || 2) + (costs.MIXING_MASTERING || 3);
      break;
    case 'multi-vocal':
      baseDays = (costs.VOCAL_RECORDING || 2) + (costs.PARAMETER_ADJUSTMENT || 2) + (costs.MIXING_MASTERING || 3) + (costs.ADDITIONAL_VOCAL || 1);
      break;
    default:
      baseDays = (costs.VOCAL_RECORDING || 2) + (costs.PARAMETER_ADJUSTMENT || 2) + (costs.MIXING_MASTERING || 3);
  }

  // オプションによる追加工数
  let additionalDays = 0;
  optionIds.forEach(optionId => {
    switch (optionId) {
      case 'additional-vocal':
        additionalDays += costs.ADDITIONAL_VOCAL || 1;
        break;
      case 'language-variation':
        additionalDays += costs.VOCAL_RECORDING || 2;
        break;
      case 'extended-version':
        additionalDays += 1;
        break;
      case 'priority-delivery':
        additionalDays += 0.5;
        break;
    }
  });

  return baseDays + additionalDays;
}

// 音楽制作の外注費計算
function calculateMusicOutsourcingCost(packageId: string, _optionIds: string[], costs: ServiceCosts): number {
  let outsourcingCost = 0;
  
  // MIDIデータ外注が必要なパッケージ
  if (['full-production', 'multi-vocal'].includes(packageId)) {
    outsourcingCost += costs.MIDI_OUTSOURCING || 15000;
  }
  
  return outsourcingCost;
}

// その他サービスの工数計算関数
function calculateCopywritingWorkDays(packageId: string, optionIds: string[], costs: ServiceCosts): number {
  let baseDays = 0;
  
  switch (packageId) {
    case 'single-article':
      baseDays = costs.SINGLE_ARTICLE || 1;
      break;
    case 'weekly-content':
      baseDays = costs.WEEKLY_CONTENT || 3;
      break;
    case 'monthly-package':
      baseDays = costs.MONTHLY_PACKAGE || 7;
      break;
    default:
      baseDays = costs.SINGLE_ARTICLE || 1;
  }

  // オプションによる追加工数
  let additionalDays = 0;
  optionIds.forEach(optionId => {
    switch (optionId) {
      case 'urgent-delivery':
        additionalDays += costs.URGENT_DELIVERY || 0.5;
        break;
      case 'multilingual':
        additionalDays += costs.MULTILINGUAL || 1;
        break;
    }
  });

  return baseDays + additionalDays;
}

function calculateDesignWorkDays(packageId: string, optionIds: string[], costs: ServiceCosts): number {
  let baseDays = 0;
  
  switch (packageId) {
    case 'logo-design':
      baseDays = costs.LOGO_DESIGN || 2;
      break;
    case 'brand-identity':
      baseDays = costs.BRAND_IDENTITY || 5;
      break;
    case 'comprehensive-brand':
      baseDays = costs.COMPREHENSIVE_BRAND || 10;
      break;
    default:
      baseDays = costs.LOGO_DESIGN || 2;
  }

  // オプションによる追加工数
  let additionalDays = 0;
  optionIds.forEach(optionId => {
    switch (optionId) {
      case 'ai-image-pack':
        additionalDays += costs.AI_IMAGE_GENERATION || 1;
        break;
      case 'animation-logo':
        additionalDays += costs.ANIMATION_WORK || 2;
        break;
    }
  });

  return baseDays + additionalDays;
}

function calculateDevelopmentWorkDays(packageId: string, optionIds: string[], costs: ServiceCosts): number {
  let baseDays = 0;
  
  switch (packageId) {
    case 'landing-page':
      baseDays = costs.LANDING_PAGE || 3;
      break;
    case 'corporate-site':
      baseDays = costs.CORPORATE_SITE || 10;
      break;
    case 'web-app':
      baseDays = costs.WEB_APPLICATION || 20;
      break;
    default:
      baseDays = costs.LANDING_PAGE || 3;
  }

  // オプションによる追加工数
  let additionalDays = 0;
  optionIds.forEach(optionId => {
    switch (optionId) {
      case 'ai-integration':
        additionalDays += costs.AI_INTEGRATION || 5;
        break;
      case 'mobile-app':
        additionalDays += costs.MOBILE_APP || 15;
        break;
    }
  });

  return baseDays + additionalDays;
}

function calculateConsultingWorkDays(packageId: string, optionIds: string[], costs: ServiceCosts): number {
  let baseDays = 0;
  
  switch (packageId) {
    case 'quick-assessment':
      baseDays = costs.QUICK_ASSESSMENT || 3;
      break;
    case 'comprehensive-strategy':
      baseDays = costs.COMPREHENSIVE_STRATEGY || 7;
      break;
    case 'implementation-support':
      baseDays = costs.IMPLEMENTATION_SUPPORT || 14;
      break;
    default:
      baseDays = costs.QUICK_ASSESSMENT || 3;
  }

  // オプションによる追加工数
  let additionalDays = 0;
  optionIds.forEach(optionId => {
    switch (optionId) {
      case 'poc-development':
        additionalDays += costs.POC_DEVELOPMENT || 5;
        break;
      case 'team-training':
        additionalDays += costs.TEAM_TRAINING || 2;
        break;
    }
  });

  return baseDays + additionalDays;
}

// 最適化された価格計算関数
export function calculateOptimizedPrice(
  config: EstimatorConfig,
  selection: EstimatorSelection
): OptimizedPriceBreakdown {
  // 詳細コスト分析
  const costAnalysis = calculateDetailedCost(config, selection);

  // 緊急度による価格調整
  let urgencyMultiplier = 1;
  if (selection.urgency === 'urgent') {
    urgencyMultiplier = 1.5; // 50%割増（残業代・急ぎ対応費用）
  } else if (selection.urgency === 'flexible') {
    urgencyMultiplier = 0.95; // 5%割引
  }

  // オプション価格計算
  const optionsPrice = selection.optionIds.reduce((total, optionId) => {
    const option = config.options.find(o => o.id === optionId);
    return total + (option?.price || 0) * selection.quantity;
  }, 0);

  // 最終価格計算
  const basePrice = Math.round(costAnalysis.finalPrice * urgencyMultiplier);
  const subtotal = basePrice + optionsPrice;
  const tax = Math.round(subtotal * FIXED_COSTS.TAX_RATE);
  const total = subtotal + tax;

  // 従来価格との比較
  const selectedPackage = config.packages.find(p => p.id === selection.packageId);
  const traditionalPrice = selectedPackage?.price || 0;
  const costReduction = traditionalPrice - total;
  const costReductionPercentage = traditionalPrice > 0 ? 
    Math.round((costReduction / traditionalPrice) * 100) : 0;

  // タイムライン計算
  const timeline = calculateOptimizedTimeline(config, selection, costAnalysis.workDays);

  return {
    basePrice,
    packagePrice: basePrice,
    optionsPrice,
    urgencyMultiplier,
    subtotal,
    tax,
    total,
    savings: Math.max(0, costReduction),
    discountPercentage: costReductionPercentage,
    timeline,
    costAnalysis,
    traditionalPrice,
    costReduction,
    costReductionPercentage
  };
}

// 最適化されたタイムライン計算
function calculateOptimizedTimeline(
  config: EstimatorConfig,
  selection: EstimatorSelection,
  workDays: number
): TimelineBreakdown {
  const unit = config.timeline.unit;

  // 作業フェーズ比率
  let developmentRatio = 0.7;
  let testingRatio = 0.2;
  let deploymentRatio = 0.1;

  // サービス別の比率調整
  switch (config.category) {
    case 'system-development':
      developmentRatio = 0.65;
      testingRatio = 0.25;
      deploymentRatio = 0.1;
      break;
    case 'consulting':
      developmentRatio = 0.6;
      testingRatio = 0.3;
      deploymentRatio = 0.1;
      break;
    case 'content-production':
      developmentRatio = 0.8;
      testingRatio = 0.15;
      deploymentRatio = 0.05;
      break;
  }

  // 緊急度による調整
  let urgencyAdjustment = 1;
  if (selection.urgency === 'urgent') {
    urgencyAdjustment = 0.7;
  } else if (selection.urgency === 'flexible') {
    urgencyAdjustment = 1.2;
  }

  const finalTimeline = workDays * urgencyAdjustment;

  // フェーズ別工数計算
  const development = Math.round(finalTimeline * developmentRatio * 10) / 10;
  const testing = Math.round(finalTimeline * testingRatio * 10) / 10;
  const deployment = Math.round(finalTimeline * deploymentRatio * 10) / 10;
  const total = Math.round(finalTimeline * 10) / 10;

  return {
    development,
    testing,
    deployment,
    total,
    unit,
    urgencyAdjustment
  };
}

// 価格フォーマット関数
export function formatPrice(price: number, currency: string = '¥'): string {
  return `${currency}${price.toLocaleString('ja-JP')}`;
}

// タイムラインフォーマット関数
export function formatTimeline(value: number, unit: 'days' | 'weeks' | 'months'): string {
  const unitNames = {
    days: '日',
    weeks: '週間',
    months: 'ヶ月'
  };
  
  if (value % 1 !== 0) {
    return `約${value}${unitNames[unit]}`;
  }
  
  return `${value}${unitNames[unit]}`;
}

// コスト分析レポート生成
export function generateCostAnalysisReport(
  config: EstimatorConfig,
  selection: EstimatorSelection
): string {
  const breakdown = calculateOptimizedPrice(config, selection);
  const { costAnalysis } = breakdown;

  return `
【コスト分析レポート】
サービス: ${config.title}
作業期間: ${costAnalysis.workDays}日
固定費: ${formatPrice(costAnalysis.fixedCost)}
外注費: ${formatPrice(costAnalysis.outsourcingCost)}
利益: ${formatPrice(costAnalysis.profitMargin)}
最終価格: ${formatPrice(breakdown.total)}
従来価格: ${formatPrice(breakdown.traditionalPrice)}
コスト削減: ${formatPrice(breakdown.costReduction)} (${breakdown.costReductionPercentage}%)
  `.trim();
}